import type { ReactNode } from 'react';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useAuth, useTenantPresentation } from '@eis/core';

/**
 * Which tenant the operator is acting for, and where that tenant's data lives.
 *
 * It reads the SESSION, which reads the TOKEN — never config.json. That is the whole point:
 * tenant context is carried by the credential, so an operator cannot end up in the wrong tenant
 * by loading the wrong build, and a support engineer looking at a screenshot can tell which
 * tenant it is from. Placement (POOL / SILO) is advisory in the token
 * (docs/standards/jwt-contract.md) — the resolver decides where a query actually goes — so the
 * badge states it as information, never as a guarantee.
 *
 * The NAME may be presented by the environment (SDD-022 D-5, T-53): `chrome.demoTenants` maps a
 * token's tenant id to the name a demo shows. The tenant is still the token's — only its label
 * changes — and the tooltip says where the label came from.
 */
export function TenantBadge(): ReactNode {
  const { t } = useTranslation('shell');
  const { tenant } = useAuth();
  const presented = useTenantPresentation();

  if (tenant === null) return null;

  const name = presented.name ?? tenant.name;
  const placementLabel =
    tenant.placement === 'unknown' ? t('tenant.placementUnknown') : tenant.placement;

  return (
    <Tooltip
      title={t(
        presented.nameSource === 'environment' ? 'tenant.tooltipPresented' : 'tenant.tooltip',
        {
          tenant: name,
          tenantId: tenant.id,
          placement: placementLabel,
          location: tenant.location ?? t('tenant.locationUnknown'),
        },
      )}
    >
      <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', flexShrink: 0 }}>
        <Typography
          variant="body2"
          data-testid="tenant-name"
          // Which tenant a screenshot came from is the first thing support asks. A name broken
          // into a vertical column of letters answers it no better than no name at all.
          noWrap
          sx={{ fontWeight: 500 }}
        >
          {name}
        </Typography>
        <Chip
          size="small"
          label={placementLabel}
          data-testid="tenant-placement"
          variant="outlined"
          sx={{ flexShrink: 0 }}
          aria-label={t('tenant.placement', { placement: placementLabel })}
        />
      </Stack>
    </Tooltip>
  );
}
